/**
 * Accès aux routes `/api/contenu/*` : types, clés de cache et hooks.
 *
 * Ce fichier vit dans `composants/` et non dans `api/hooks.ts` pour une raison
 * de circonstance : `hooks.ts` est écrit en parallèle par l'écran des
 * prospects, et deux plumes sur un même fichier produisent des conflits sans
 * rien apporter. Il en reprend fidèlement les conventions — clés regroupées,
 * types recopiés depuis le contrat HTTP, mutations qui invalident ce qu'elles
 * ont rendu faux.
 *
 * Les types décrivent ce que rendent les routes (`vueX` des dépôts), pas les
 * lignes SQLite qui les alimentent.
 */
import { type UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ErreurApi, type OptionsRequete, api, appelerApi } from "../api/client";

// ---------------------------------------------------------------------------
// Formes renvoyées par l'API
// ---------------------------------------------------------------------------

export type CategoriePrestation = "interieur" | "metallerie";

export type Prestation = {
  id: number;
  slug: string;
  nom: string;
  categorie: CategoriePrestation;
  resume: string;
  reponseDirecte: string;
  contenu: string;
  icone: string;
  ordre: number;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
  updatedAt: string;
};

export type Commune = {
  id: number;
  slug: string;
  nom: string;
  codePostal: string;
  codeInsee: string;
  lat: number;
  lng: number;
  distanceKm: number;
  contenu: string;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
  ordre: number;
};

export type Realisation = {
  id: number;
  chantierId: number | null;
  slug: string;
  titre: string;
  resume: string;
  contenu: string;
  communeId: number | null;
  prestations: number[];
  medias: number[];
  budgetIndicatif: string;
  dureeJours: number | null;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
  publishedAt: string | null;
};

export type ScopeFaq = "globale" | "prestation" | "commune";

export type Question = {
  id: number;
  question: string;
  reponse: string;
  scope: ScopeFaq;
  scopeId: number | null;
  ordre: number;
  isPublished: boolean;
};

export type SourceAvis = "google" | "manuel";

export type Avis = {
  id: number;
  auteur: string;
  note: number;
  texte: string;
  source: SourceAvis;
  communeId: number | null;
  date: string;
  isPublished: boolean;
};

export type Reglages = Record<string, string>;

// --- Garde-fou éditorial ---------------------------------------------------

/**
 * Codes de `CODES_MANQUEMENT` dans `@delsa/shared`. Recopiés ici comme type
 * seulement : l'admin les affiche, elle ne les produit jamais.
 */
export type CodeManquement =
  | "contenu_trop_court"
  | "faq_insuffisante"
  | "reponse_directe_absente"
  | "reponse_directe_dupliquee";

export type Manquement = {
  code: CodeManquement;
  /** Phrase rédigée côté serveur, affichable telle quelle. */
  message: string;
};

export type RapportPage = {
  type: "prestation" | "commune";
  chemin: string;
  titre: string;
  publiee: boolean;
  mots: number;
  nombreFaq: number;
  manquements: Manquement[];
};

export type SeuilsQualite = {
  motsMinPrestation: number;
  motsMinCommune: number;
  similariteMax: number;
  faqMin: number;
};

export type RapportQualite = {
  pages: RapportPage[];
  /** Nombre de pages **publiées** en défaut, c'est-à-dire qui feront échouer le build. */
  bloquants: number;
  seuils: SeuilsQualite;
};

// ---------------------------------------------------------------------------
// Clés de cache
// ---------------------------------------------------------------------------

export const clesContenu = {
  liste: (ressource: string) => ["contenu", ressource] as const,
  qualite: ["contenu", "qualite"] as const,
  reglages: ["contenu", "reglages"] as const,
};

/** Ressources CRUD, avec la clé sous laquelle l'API range leur collection. */
export const RESSOURCES = {
  prestations: { cle: "prestation", cleListe: "prestations" },
  communes: { cle: "commune", cleListe: "communes" },
  realisations: { cle: "realisation", cleListe: "realisations" },
  faq: { cle: "question", cleListe: "faq" },
  avis: { cle: "avis", cleListe: "avis" },
} as const;

export type NomRessource = keyof typeof RESSOURCES;

// ---------------------------------------------------------------------------
// Lecture
// ---------------------------------------------------------------------------

function useListe<T>(ressource: NomRessource): UseQueryResult<T[], ErreurApi> {
  const { cleListe } = RESSOURCES[ressource];

  return useQuery<T[], ErreurApi>({
    queryKey: clesContenu.liste(ressource),
    queryFn: async () => {
      const charge = await api.get<Record<string, T[]>>(`/api/contenu/${ressource}`);
      // La clé est celle du contrat ; une API qui changerait d'avis doit
      // donner une liste vide, pas faire planter le rendu sur `undefined`.
      return charge[cleListe] ?? [];
    },
  });
}

export const usePrestations = () => useListe<Prestation>("prestations");
export const useCommunes = () => useListe<Commune>("communes");
export const useRealisations = () => useListe<Realisation>("realisations");
export const useQuestions = () => useListe<Question>("faq");
export const useAvis = () => useListe<Avis>("avis");

/**
 * Rapport de qualité éditoriale, produit par les mêmes fonctions que celles qui
 * font échouer la publication.
 *
 * Il est volontairement considéré comme périmé d'emblée : chaque enregistrement
 * l'invalide, et la duplication des réponses directes se juge **entre** pages —
 * modifier une prestation peut donc changer le verdict d'une autre.
 */
export function useQualite(): UseQueryResult<RapportQualite, ErreurApi> {
  return useQuery<RapportQualite, ErreurApi>({
    queryKey: clesContenu.qualite,
    queryFn: () => api.get<RapportQualite>("/api/contenu/qualite"),
    staleTime: 0,
  });
}

export function useReglages(): UseQueryResult<Reglages, ErreurApi> {
  return useQuery<Reglages, ErreurApi>({
    queryKey: clesContenu.reglages,
    queryFn: async () => {
      const { reglages } = await api.get<{ reglages: Reglages }>("/api/contenu/reglages");
      return reglages;
    },
  });
}

// ---------------------------------------------------------------------------
// Écriture
// ---------------------------------------------------------------------------

export type Enregistrement = {
  /** `null` pour une création, l'identifiant pour une mise à jour partielle. */
  id: number | null;
  champs: Record<string, unknown>;
};

/**
 * Crée ou met à jour, puis invalide la liste **et** le rapport de qualité.
 *
 * Oublier la seconde invalidation laisserait l'écran afficher les manquements
 * d'avant la correction : l'utilisateur croirait son texte encore fautif, ou
 * pire, encore conforme.
 */
export function useEnregistrer<T>(ressource: NomRessource) {
  const cache = useQueryClient();
  const { cle } = RESSOURCES[ressource];

  return useMutation<T | undefined, ErreurApi, Enregistrement>({
    mutationFn: async ({ id, champs }) => {
      const chemin = id === null ? `/api/contenu/${ressource}` : `/api/contenu/${ressource}/${id}`;
      const charge =
        id === null
          ? await api.post<Record<string, T>>(chemin, champs)
          : await api.patch<Record<string, T>>(chemin, champs);
      return charge[cle];
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: clesContenu.liste(ressource) });
      void cache.invalidateQueries({ queryKey: clesContenu.qualite });
    },
  });
}

export function useSupprimer(ressource: NomRessource) {
  const cache = useQueryClient();

  return useMutation<void, ErreurApi, number>({
    mutationFn: async (id) => {
      await api.supprimer<{ ok: boolean }>(`/api/contenu/${ressource}/${id}`);
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: clesContenu.liste(ressource) });
      void cache.invalidateQueries({ queryKey: clesContenu.qualite });
    },
  });
}

/**
 * `PUT` est le seul verbe de l'API que le client HTTP partagé ne déclare pas :
 * aucune autre route ne l'emploie. Élargir son type reviendrait à modifier
 * `api/client.ts`, écrit en parallèle par un autre écran. On force donc ici la
 * méthode manquante — le corps de `appelerApi` la transmet telle quelle à
 * `fetch` — pour continuer de bénéficier du cookie de session, de la
 * redirection sur 401 et des erreurs par champ.
 */
const PUT = "PUT" as unknown as NonNullable<OptionsRequete["methode"]>;

/**
 * Enregistre des réglages. L'API écrit clé par clé : les clés absentes de la
 * charge utile ne sont pas touchées, ce qui permet de n'envoyer que ce qui a
 * réellement changé.
 */
export function useEnregistrerReglages() {
  const cache = useQueryClient();

  return useMutation<Reglages, ErreurApi, Reglages>({
    mutationFn: async (modifications) => {
      const { reglages } = await appelerApi<{ reglages: Reglages }>("/api/contenu/reglages", {
        methode: PUT,
        corps: modifications,
      });
      return reglages;
    },
    onSuccess: (reglages) => {
      cache.setQueryData(clesContenu.reglages, reglages);
    },
  });
}
